import React, { useCallback, useEffect, useState } from 'react'
import { Table, Tag, Button, Input, Popconfirm, Space, Tooltip } from 'antd'
import styled from 'styled-components'
import { fetchCallSlots, updateCallSlot } from './api'
import { STATUS_META, formatDuration, formatIst, outcomeText, slotActions, summaryParts } from './format'

const REFRESH_MS = 20000

const Panel = styled.section`
  margin: 0 20px 20px;
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`
const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`
const Sub = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
`
const Muted = styled.div`
  font-size: 12px;
  color: #6b7280;
  overflow-wrap: anywhere;
`
const Recording = styled.div`
  & + & {
    margin-top: 6px;
  }
  audio {
    display: block;
    width: 100%;
    min-width: 180px;
    height: 32px;
  }
`

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #dc2626;
`

/**
 * Keeps the first signed link it is given: the list refreshes every 20 s with
 * freshly signed URLs, and swapping `src` would cut off a recording mid-play.
 * A link lasts an hour; reopening the page signs new ones.
 */
const RecordingPlayer = ({ url, label }) => {
  const [src] = useState(url)
  return <audio controls preload="none" src={src} aria-label={label} />
}

/**
 * One candidate row's editor: the call time (India time, native picker — the
 * value is admin-node's own "YYYY-MM-DDTHH:MM", so no timezone conversion can
 * shift it) and the mobile number.
 */
const useRowEditor = onSaved => {
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState({ when: '', phone: '' })
  const [errors, setErrors] = useState({})
  const [savingId, setSavingId] = useState(null)

  const run = async (slot, update) => {
    setSavingId(slot.slotId)
    setErrors(prev => ({ ...prev, [slot.slotId]: null }))
    const message = await updateCallSlot(slot.slotId, update)
    setSavingId(null)
    if (message) {
      setErrors(prev => ({ ...prev, [slot.slotId]: message }))
      return
    }
    setEditingId(null)
    await onSaved()
  }

  const start = slot => {
    setDraft({ when: slot.scheduledAtIst, phone: slot.phone || '' })
    setErrors(prev => ({ ...prev, [slot.slotId]: null }))
    setEditingId(slot.slotId)
  }

  const save = slot => {
    const update = {}
    if (draft.when && draft.when !== slot.scheduledAtIst) update.scheduledAtIst = draft.when
    const phone = draft.phone.replace(/[\s()-]/g, '')
    if (phone && phone !== (slot.phone || '')) update.phone = phone
    if (Object.keys(update).length === 0) {
      setEditingId(null)
      return
    }
    run(slot, update)
  }

  return { editingId, draft, setDraft, errors, savingId, start, save, cancelEdit: () => setEditingId(null), run }
}

/**
 * Call schedule of one on-call AI Interview: when each candidate will be (or
 * was) called, what happened, and inline reschedule / change number / cancel /
 * call again. Renders nothing for assessments that are not on call.
 */
const CallSchedulePanel = ({ mapId }) => {
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    if (!mapId) return
    try {
      setData(await fetchCallSlots(mapId, page))
      setLoadError(null)
    } catch (error) {
      setLoadError('Could not load the call schedule.')
    }
  }, [mapId, page])

  useEffect(() => {
    load()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  const editor = useRowEditor(load)

  if (loadError && !data) {
    return (
      <Panel>
        <Title>Call schedule</Title>
        <ErrorText role="alert">{loadError}</ErrorText>
        <Button size="small" style={{ marginTop: 8 }} onClick={load}>Try again</Button>
      </Panel>
    )
  }
  if (!data?.interview?.onCall) return null

  const { interview } = data
  const minutes = Math.round((interview.interviewDurationSec || 600) / 60)

  const columns = [
    {
      title: 'Candidate',
      key: 'candidate',
      width: '18%',
      render: (_, slot) => (
        <>
          <div style={{ fontWeight: 600, color: '#111827' }}>{slot.name || slot.email}</div>
          {slot.name && <Muted>{slot.email}</Muted>}
        </>
      ),
    },
    {
      title: 'Mobile',
      key: 'phone',
      width: '13%',
      render: (_, slot) => editor.editingId === slot.slotId ? (
        <Input
          type="tel"
          aria-label="Mobile number"
          placeholder="+919876543210"
          value={editor.draft.phone}
          onChange={e => editor.setDraft(d => ({ ...d, phone: e.target.value }))}
        />
      ) : (slot.phone || <Muted>No number</Muted>),
    },
    {
      title: 'Call time (IST)',
      key: 'when',
      width: '16%',
      render: (_, slot) => editor.editingId === slot.slotId ? (
        <>
          <Input
            type="datetime-local"
            aria-label="Call time, India time"
            min={interview.windowStartIst}
            max={interview.windowEndIst}
            value={editor.draft.when}
            onChange={e => editor.setDraft(d => ({ ...d, when: e.target.value }))}
          />
          <Muted style={{ marginTop: 4 }}>At least 5 min from now; the call must end by {formatIst(interview.windowEndIst)}</Muted>
        </>
      ) : <span style={{ fontWeight: 500 }}>{formatIst(slot.scheduledAtIst)}</span>,
    },
    {
      title: 'Status',
      key: 'status',
      width: '9%',
      render: (_, slot) => {
        const meta = STATUS_META[slot.status] || { label: slot.status, color: 'default' }
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
    },
    {
      title: 'Last outcome',
      key: 'outcome',
      width: '12%',
      render: (_, slot) => (
        <>
          <div>{outcomeText(slot) || <Muted>—</Muted>}</div>
          {slot.attempts > 0 && <Muted>{slot.attempts} {slot.attempts === 1 ? 'attempt' : 'attempts'}</Muted>}
          {editor.errors[slot.slotId] && <ErrorText role="alert">{editor.errors[slot.slotId]}</ErrorText>}
        </>
      ),
    },
    {
      title: 'Recording',
      key: 'recordings',
      width: '18%',
      render: (_, slot) => (slot.recordings?.length ? slot.recordings.map((r, i) => (
        <Recording key={r.recordedAt}>
          {slot.recordings.length > 1 && <Muted>Call {i + 1}{r.durationSec ? ` · ${formatDuration(r.durationSec)}` : ''}</Muted>}
          <RecordingPlayer url={r.url} label={`Call recording ${i + 1} for ${slot.name || slot.email}`} />
        </Recording>
      )) : <Muted>—</Muted>),
    },
    {
      title: '',
      key: 'actions',
      width: '14%',
      align: 'right',
      render: (_, slot) => {
        const { canEdit, canCancel, canRequeue } = slotActions(slot)
        const busy = editor.savingId === slot.slotId
        if (editor.editingId === slot.slotId) {
          return (
            <Space size={6} wrap style={{ justifyContent: 'flex-end' }}>
              <Button type="primary" size="small" loading={busy} onClick={() => editor.save(slot)}>Save call</Button>
              <Button size="small" disabled={busy} onClick={editor.cancelEdit}>Discard</Button>
            </Space>
          )
        }
        return (
          <Space size={6} wrap style={{ justifyContent: 'flex-end' }}>
            {canEdit && <Button size="small" disabled={busy} onClick={() => editor.start(slot)}>Edit</Button>}
            {canRequeue && (
              <Tooltip title="Queue the call again at the next available time">
                <Button size="small" loading={busy} onClick={() => editor.run(slot, {})}>Call again</Button>
              </Tooltip>
            )}
            {canCancel && (
              <Popconfirm
                title="Cancel this candidate's call?"
                okText="Cancel call"
                cancelText="Keep"
                onConfirm={() => editor.run(slot, { action: 'cancel' })}
              >
                <Button size="small" danger disabled={busy}>Cancel</Button>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <Panel aria-labelledby="call-schedule-title">
      <Head>
        <div>
          <Title id="call-schedule-title">Call schedule</Title>
          <Sub>
            Candidates are phoned between {formatIst(interview.windowStartIst)} and {formatIst(interview.windowEndIst)} IST,
            about {minutes} min per call. Suggested times fall between 10:00 AM and 7:00 PM; you can move a call to any time in the window. Times refresh automatically.
          </Sub>
        </div>
        <Space size={4} wrap>
          {summaryParts(data.statusCounts).map(({ status, count }) => (
            <Tag key={status} color={STATUS_META[status].color}>
              {count} {STATUS_META[status].label.toLowerCase()}
            </Tag>
          ))}
        </Space>
      </Head>
      <Table
        rowKey="slotId"
        size="small"
        columns={columns}
        dataSource={data.slots}
        locale={{ emptyText: 'Calls are planned as candidates finish being assigned, usually within a minute.' }}
        pagination={data.total > data.pageSize ? {
          current: page,
          pageSize: data.pageSize,
          total: data.total,
          showSizeChanger: false,
          onChange: setPage,
        } : false}
      />
    </Panel>
  )
}

export default CallSchedulePanel
